import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { Link } from "react-router-dom";

const HEADLINE = [
  { text: "FIERCE", accent: false },
  { text: "ATTIRE", accent: true },
  { text: "FOR THE", outline: true },
  { text: "BOLD.", accent: false },
];

const wordVariants = {
  hidden: { y: 80, opacity: 0, rotate: 4 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    rotate: 0,
    transition: { delay: 0.15 + i * 0.12, type: "spring", stiffness: 90, damping: 14 },
  }),
};

export const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-white">
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-2 lg:items-stretch lg:px-12 lg:py-28">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-eyebrow"
            className="mb-6 flex items-center gap-2 font-body text-xs font-medium uppercase tracking-[0.3em] text-tiger"
          >
            <Flame size={14} /> www.tiger-clothing.co.uk
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display text-5xl uppercase leading-[0.95] tracking-wide text-zinc-900 sm:text-7xl lg:text-8xl">
            {HEADLINE.map((word, i) => (
              <span key={word.text} className="mr-4 inline-block overflow-hidden align-top">
                <motion.span
                  custom={i}
                  variants={wordVariants}
                  initial="hidden"
                  animate="visible"
                  className={`inline-block ${word.accent ? "text-tiger" : word.outline ? "text-outline" : ""}`}
                >
                  {word.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="mt-6 max-w-md font-body text-sm leading-relaxed text-zinc-500 sm:text-base"
            data-testid="hero-subtext"
          >
            Premium print-on-demand streetwear, fulfilled through Printful. Heavyweight cotton,
            razor-sharp tiger graphics, shipped worldwide from the UK.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#shop"
              data-testid="hero-explore-drop-btn"
              className="group flex items-center gap-2 bg-tiger px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-black shadow-[4px_4px_0px_#0A0A0B] transition-all hover:-translate-y-1 hover:bg-tiger-hover"
            >
              Explore Drop 01
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/gb/en/lookbook.html"
              data-testid="hero-lookbook-btn"
              className="border-2 border-zinc-300 px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-zinc-900 transition-colors hover:border-tiger hover:text-tiger"
            >
              View Lookbook
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-12 flex gap-10 border-t border-zinc-200 pt-6"
            data-testid="hero-stats"
          >
            {[
              ["240", "GSM Cotton"],
              ["2-5", "Day Fulfilment"],
              ["30", "Day Returns"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-display text-2xl text-tiger sm:text-3xl">{num}</p>
                <p className="mt-1 font-body text-xs uppercase tracking-widest text-zinc-500">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="relative lg:-my-28"
          data-testid="hero-image-wrap"
        >
          <video
            src="/assets/hero-loop.mp4"
            poster="/assets/mockup-hero.jpg"
            autoPlay
            muted
            loop
            playsInline
            aria-label="Model wearing the Tiger Clothing orange tiger tee"
            className="h-[420px] w-full object-cover object-top sm:h-[560px] lg:absolute lg:inset-0 lg:h-full"
            data-testid="hero-video"
          />
          <div className="animate-float-badge absolute -bottom-6 -left-6 bg-tiger px-6 py-4 shadow-[6px_6px_0px_#0A0A0B]" data-testid="hero-floating-badge">
            <p className="font-display text-xl uppercase text-black">Drop 01</p>
            <p className="font-body text-xs font-semibold uppercase tracking-widest text-black/70">Live Now</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
